const toNumber = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const clampPercent = (value) => Math.max(0, Math.min(100, toNumber(value)));

const startOfWeekMonday = (dateInput) => {
    const date = new Date(dateInput || Date.now());
    if (Number.isNaN(date.getTime())) {
        return startOfWeekMonday(Date.now());
    }

    const monday = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
    const day = monday.getUTCDay();
    const diff = (day + 6) % 7;
    monday.setUTCDate(monday.getUTCDate() - diff);
    return monday;
};

export const getWeekStartDateForPreset = ({ preset, customDate }) => {
    if (preset === "custom" && customDate) {
        return startOfWeekMonday(customDate);
    }

    const monday = startOfWeekMonday(Date.now());
    if (preset === "previous") {
        monday.setUTCDate(monday.getUTCDate() - 7);
    }
    return monday;
};

export const getWeekStartIso = ({ preset, customDate }) => {
    return getWeekStartDateForPreset({ preset, customDate }).toISOString();
};

export const formatWeekLabel = (isoDate) => {
    if (!isoDate) return "Current week";
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return "Current week";

    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    }).format(date);
};

export const normalizeWeeklyReport = (report = {}) => ({
    id: report._id || report.id || "",
    studentId: report.studentId || "",
    weekStartDate: report.weekStartDate || "",
    readingMinutes: Math.max(0, toNumber(report.readingMinutes)),
    averageAccuracy: clampPercent(report.averageAccuracy),
    averageWpm: Math.max(0, toNumber(report.averageWpm)),
    completionRate: clampPercent(report.completionRate),
    assignmentsAssigned: Math.max(0, toNumber(report.assignmentsAssigned)),
    assignmentsCompleted: Math.max(0, toNumber(report.assignmentsCompleted)),
    quizPerformance: clampPercent(report.quizPerformance),
    readingConsistency: clampPercent(report.readingConsistency),
    createdAt: report.createdAt || "",
    updatedAt: report.updatedAt || "",
});

export const normalizeWeeklyReportResponse = (response = {}) => {
    const rawReport = response?.data?.report || null;
    if (!rawReport) return null;
    return normalizeWeeklyReport(rawReport);
};

export const normalizeStudentOption = (student = {}) => {
    const id = student.id || student._id || student.studentId || "";
    const firstName = student.firstName || "";
    const lastName = student.lastName || "";
    return {
        id,
        name: `${firstName} ${lastName}`.trim() || "Student",
    };
};

export const formatMetricValue = (key, value) => {
    if (key === "readingMinutes") return `${value} min`;
    if (key === "averageAccuracy") return `${value}%`;
    if (key === "averageWpm") return `${value} WPM`;
    if (key === "completionRate") return `${value}%`;
    if (key === "quizPerformance") return `${value}%`;
    if (key === "readingConsistency") return `${value}%`;
    return String(value);
};

export const buildMetricCards = (report) => {
    if (!report) return [];

    return [
        { key: "readingMinutes", label: "Reading Minutes", value: report.readingMinutes },
        { key: "averageAccuracy", label: "Average Accuracy", value: report.averageAccuracy },
        { key: "averageWpm", label: "Average WPM", value: report.averageWpm },
        { key: "completionRate", label: "Completion Rate", value: report.completionRate },
        { key: "assignmentsAssigned", label: "Assignments Assigned", value: report.assignmentsAssigned },
        { key: "assignmentsCompleted", label: "Assignments Completed", value: report.assignmentsCompleted },
        { key: "quizPerformance", label: "Quiz Performance", value: report.quizPerformance },
        { key: "readingConsistency", label: "Reading Consistency", value: report.readingConsistency },
    ];
};

export const buildReportCharts = (report) => {
    if (!report) return [];

    return [
        {
            key: "readingMinutes",
            title: "Reading Minutes",
            value: report.readingMinutes,
            max: Math.max(300, report.readingMinutes),
            displayValue: `${report.readingMinutes} min`,
        },
        {
            key: "averageAccuracy",
            title: "Accuracy",
            value: report.averageAccuracy,
            max: 100,
            displayValue: `${report.averageAccuracy}%`,
        },
        {
            key: "averageWpm",
            title: "WPM",
            value: report.averageWpm,
            max: Math.max(180, report.averageWpm),
            displayValue: `${report.averageWpm} WPM`,
        },
        {
            key: "assignmentCompletion",
            title: "Assignment Completion",
            value: report.assignmentsCompleted,
            max: Math.max(1, report.assignmentsAssigned),
            displayValue: `${report.assignmentsCompleted}/${report.assignmentsAssigned}`,
        },
        {
            key: "readingConsistency",
            title: "Reading Consistency",
            value: report.readingConsistency,
            max: 100,
            displayValue: `${report.readingConsistency}%`,
        },
    ];
};

export const getReportErrorMessage = (apiError = {}) => {
    const status = apiError?.status || apiError?.response?.status || 0;
    const message = apiError?.message || apiError?.response?.data?.error || "";

    if (status === 403) {
        return "You do not have permission to view this report.";
    }

    if (status === 404) {
        return "Report not found.";
    }

    return message || "Something went wrong. Please try again.";
};
